import React, { useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  ScrollView,
  TextInput,
  Platform,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { AdminColors } from '../../../constants/adminColors';
import FontFamily from '../../../../../constants/fonts';
import LocationFilterDropdown, { FilterOption } from './LocationFilterDropdown';

export interface MoreAttributesFilterState {
  ownership: string[];
  coeResource: string[];
  secondaryCoeResource: string[];
  regionLead: string[];
  stateProvince: string[];
  warehouseSystem: string[];
  labor: string[];
  union: string[];
  supplier: string[];
  isDuo: string[];
  yearBuiltMin: string;
  yearBuiltMax: string;
  pickedVolumeMin: string;
  pickedVolumeMax: string;
  totalSqftMin: string;
  totalSqftMax: string;
  operationalSqftMin: string;
  operationalSqftMax: string;
  docksMin: string;
  docksMax: string;
  docksEngMin: string;
  docksEngMax: string;
  docksNoEngMin: string;
  docksNoEngMax: string;
  driveInMin: string;
  driveInMax: string;
  bottleLinesMin: string;
  bottleLinesMax: string;
  canLinesMin: string;
  canLinesMax: string;
  build1: string[];
  build2: string[];
  bulkSite: string[];
}

export const createEmptyMoreAttributesFilters = (): MoreAttributesFilterState => ({
  ownership: [],
  coeResource: [],
  secondaryCoeResource: [],
  regionLead: [],
  stateProvince: [],
  warehouseSystem: [],
  labor: [],
  union: [],
  supplier: [],
  isDuo: [],
  yearBuiltMin: '',
  yearBuiltMax: '',
  pickedVolumeMin: '',
  pickedVolumeMax: '',
  totalSqftMin: '',
  totalSqftMax: '',
  operationalSqftMin: '',
  operationalSqftMax: '',
  docksMin: '',
  docksMax: '',
  docksEngMin: '',
  docksEngMax: '',
  docksNoEngMin: '',
  docksNoEngMax: '',
  driveInMin: '',
  driveInMax: '',
  bottleLinesMin: '',
  bottleLinesMax: '',
  canLinesMin: '',
  canLinesMax: '',
  build1: [],
  build2: [],
  bulkSite: [],
});

export const getMoreAttributesActiveCount = (value: MoreAttributesFilterState): number => {
  const rangeActive = (min: string, max: string) => min.trim() !== '' || max.trim() !== '';
  let count = 0;

  if (value.ownership.length > 0) count++;
  if (value.regionLead.length > 0) count++;
  if (value.stateProvince.length > 0) count++;
  if (value.warehouseSystem.length > 0) count++;
  if (value.labor.length > 0) count++;
  if (value.union.length > 0) count++;
  if (value.supplier.length > 0) count++;
  if (rangeActive(value.yearBuiltMin, value.yearBuiltMax)) count++;
  if (rangeActive(value.pickedVolumeMin, value.pickedVolumeMax)) count++;
  if (rangeActive(value.totalSqftMin, value.totalSqftMax)) count++;
  if (rangeActive(value.operationalSqftMin, value.operationalSqftMax)) count++;
  if (rangeActive(value.docksMin, value.docksMax)) count++;
  if (rangeActive(value.docksEngMin, value.docksEngMax)) count++;
  if (rangeActive(value.docksNoEngMin, value.docksNoEngMax)) count++;
  if (rangeActive(value.driveInMin, value.driveInMax)) count++;
  if (rangeActive(value.bottleLinesMin, value.bottleLinesMax)) count++;
  if (rangeActive(value.canLinesMin, value.canLinesMax)) count++;
  if (value.build1.length > 0) count++;
  if (value.build2.length > 0) count++;
  if (value.bulkSite.length > 0) count++;

  return count;
};

interface MoreAttributesDrawerProps {
  visible: boolean;
  onClose: () => void;
  onApply: (value: MoreAttributesFilterState) => void;
  onReset: () => void;
  value: MoreAttributesFilterState;
  onChange: (value: MoreAttributesFilterState) => void;
  ownershipOptions: FilterOption[];
  coeResourceOptions: FilterOption[];
  secondaryCoeResourceOptions: FilterOption[];
  regionLeadOptions: FilterOption[];
  stateProvinceOptions: FilterOption[];
  warehouseSystemOptions: FilterOption[];
  laborOptions: FilterOption[];
  supplierOptions: FilterOption[];
  buildSite1Options: FilterOption[];
  buildSite2Options: FilterOption[];
  bulkSiteOptions: FilterOption[];
  ownershipSearchOptions?: (searchText: string) => Promise<FilterOption[]>;
  regionLeadSearchOptions?: (searchText: string) => Promise<FilterOption[]>;
  stateProvinceSearchOptions?: (searchText: string) => Promise<FilterOption[]>;
  warehouseSystemSearchOptions?: (searchText: string) => Promise<FilterOption[]>;
  laborSearchOptions?: (searchText: string) => Promise<FilterOption[]>;
  supplierSearchOptions?: (searchText: string) => Promise<FilterOption[]>;
  buildSite1SearchOptions?: (searchText: string) => Promise<FilterOption[]>;
  buildSite2SearchOptions?: (searchText: string) => Promise<FilterOption[]>;
  bulkSiteSearchOptions?: (searchText: string) => Promise<FilterOption[]>;
}

const duoOptions: FilterOption[] = [
  { id: 'true', displayText: 'Enabled' },
  { id: 'false', displayText: 'Disabled' },
];

const unionOptions: FilterOption[] = [
  { id: 'Union', displayText: 'Union' },
  { id: 'Non-Union', displayText: 'Non-Union' },
  { id: 'Unknown', displayText: 'Unknown' },
];

type MoreAttributeField = {
  key: string;
  label: string;
  type: 'multi' | 'range' | 'text';
};

const NumberField: React.FC<{
  label: string;
  minValue: string;
  maxValue: string;
  onChangeMin: (value: string) => void;
  onChangeMax: (value: string) => void;
  placeholderMin?: string;
  placeholderMax?: string;
}> = ({
  label,
  minValue,
  maxValue,
  onChangeMin,
  onChangeMax,
  placeholderMin = 'Min',
  placeholderMax = 'Max',
}) => (
  <View style={styles.rangeGroup}>
    <Text style={styles.fieldLabel}>{label}</Text>
    <View style={styles.rangeRow}>
      <TextInput
        style={styles.rangeInput}
        keyboardType="numeric"
        value={minValue}
        onChangeText={onChangeMin}
        placeholder={placeholderMin}
        placeholderTextColor={AdminColors.textMuted}
      />
      <Text style={styles.rangeDash}>to</Text>
      <TextInput
        style={styles.rangeInput}
        keyboardType="numeric"
        value={maxValue}
        onChangeText={onChangeMax}
        placeholder={placeholderMax}
        placeholderTextColor={AdminColors.textMuted}
      />
    </View>
  </View>
);

const MoreAttributesDrawer: React.FC<MoreAttributesDrawerProps> = ({
  visible,
  onClose,
  onApply,
  onReset,
  value,
  onChange,
  ownershipOptions,
  coeResourceOptions,
  secondaryCoeResourceOptions,
  regionLeadOptions,
  stateProvinceOptions,
  warehouseSystemOptions,
  laborOptions,
  supplierOptions,
  buildSite1Options,
  buildSite2Options,
  bulkSiteOptions,
  ownershipSearchOptions,
  regionLeadSearchOptions,
  stateProvinceSearchOptions,
  warehouseSystemSearchOptions,
  laborSearchOptions,
  supplierSearchOptions,
  buildSite1SearchOptions,
  buildSite2SearchOptions,
  bulkSiteSearchOptions,
}) => {
  const [search, setSearch] = useState('');

  const appliedCount = useMemo(() => getMoreAttributesActiveCount(value), [value]);

  const filteredSections = useMemo(() => {
    const query = search.trim().toLowerCase();

    const sections: Array<{
      title: string;
      description: string;
      fields: MoreAttributeField[];
    }> = [
      {
        title: 'Geography and Ownership',
        description: 'Filter by ownership and state or province.',
        fields: [
          { key: 'stateProvince', label: 'State/Province', type: 'multi' as const },
          { key: 'ownership', label: 'Ownership', type: 'multi' as const },
        ],
      },
      {
        title: 'Facility Profile',
        description: 'Filter by operating profile and assigned resources.',
        fields: [
          { key: 'warehouseSystem', label: 'Warehouse System', type: 'multi' as const },
          { key: 'labor', label: 'Labor Model', type: 'multi' as const },
          { key: 'supplier', label: '3PL Supplier', type: 'multi' as const },
          { key: 'yearBuilt', label: 'Year Built', type: 'range' as const },
        ],
      },
      {
        title: 'Capacity and Scale',
        description: 'Compare volume, footprint, and dock counts.',
        fields: [
          { key: 'pickedVolume', label: 'Picked Volume', type: 'range' as const },
          { key: 'totalSqft', label: 'Total Building Sqft', type: 'range' as const },
          { key: 'operationalSqft', label: 'Operational Sqft', type: 'range' as const },
          { key: 'docks', label: 'Total Docks', type: 'range' as const },
        ],
      },
      {
        title: 'Docks and Production',
        description: 'Narrow locations by dock and line counts.',
        fields: [
          { key: 'docksEng', label: 'Docks with ENG Controls', type: 'range' as const },
          { key: 'docksNoEng', label: 'Docks without ENG Controls', type: 'range' as const },
          { key: 'driveIn', label: 'Drive-in Bays', type: 'range' as const },
          { key: 'bottleLines', label: 'Bottle Lines', type: 'range' as const },
          { key: 'canLines', label: 'Can Lines', type: 'range' as const },
        ],
      },
      {
        title: 'Contacts',
        description: 'Filter by the primary regional contact.',
        fields: [
          { key: 'regionLead', label: 'Region Lead', type: 'multi' as const },
        ],
      },
      {
        title: 'Network Relationships',
        description: 'Filter by connected build sites and bulk site relationships.',
        fields: [
          { key: 'build1', label: 'Build Site 1', type: 'text' as const },
          { key: 'build2', label: 'Build Site 2', type: 'text' as const },
          { key: 'bulkSite', label: 'Centralized Bulk Site', type: 'text' as const },
        ],
      },
    ];

    if (!query) {
      return sections;
    }

    return sections
      .map(section => {
        const titleMatches = section.title.toLowerCase().includes(query) || section.description.toLowerCase().includes(query);
        const fields = section.fields.filter(field => titleMatches || field.label.toLowerCase().includes(query));
        return fields.length > 0 ? { ...section, fields } : null;
      })
      .filter((section): section is typeof sections[number] => section !== null);
  }, [search]);

  const update = (patch: Partial<MoreAttributesFilterState>) => onChange({ ...value, ...patch });

  const renderFieldLabel = (label: string) => <Text style={styles.fieldLabel}>{label}</Text>;

  const handleReset = () => {
    onReset();
    setSearch('');
  };

  const handleApply = () => {
    onApply(value);
    onClose();
  };

  const renderMulti = (
    key: keyof MoreAttributesFilterState,
    label: string,
    options: FilterOption[],
    searchOptions?: (searchText: string) => Promise<FilterOption[]>,
  ) => {
    const selectedValues = value[key] as string[];
    const selectedOptions = options.filter(option => selectedValues.includes(String(option.id)));

    const onMultiChange = (next: FilterOption | FilterOption[] | null) => {
      if (Array.isArray(next)) {
        update({ [key]: next.map(option => String(option.id)) } as Partial<MoreAttributesFilterState>);
      } else if (next) {
        update({ [key]: [String(next.id)] } as Partial<MoreAttributesFilterState>);
      } else {
        update({ [key]: [] } as Partial<MoreAttributesFilterState>);
      }
    };

    return (
      <View style={styles.fieldGroup}>
        {renderFieldLabel(label)}
        <LocationFilterDropdown
          label={label}
          options={options}
          value={selectedOptions.length > 0 ? selectedOptions : null}
          onChange={onMultiChange}
          isMulti
          showLabelInTrigger={false}
          placeholder="All"
          searchOptions={searchOptions}
        />
      </View>
    );
  };

  const renderSectionField = (field: MoreAttributeField) => {
    switch (field.key) {
      case 'ownership':
        return renderMulti('ownership', 'Ownership', ownershipOptions, ownershipSearchOptions);
      case 'stateProvince':
        return renderMulti('stateProvince', 'State/Province', stateProvinceOptions, stateProvinceSearchOptions);
      case 'regionLead':
        return renderMulti('regionLead', 'Region Lead', regionLeadOptions, regionLeadSearchOptions);
      case 'warehouseSystem':
        return renderMulti('warehouseSystem', 'Warehouse System', warehouseSystemOptions, warehouseSystemSearchOptions);
      case 'labor':
        return renderMulti('labor', 'Labor Model', laborOptions, laborSearchOptions);
      case 'supplier':
        return renderMulti('supplier', '3PL Supplier', supplierOptions, supplierSearchOptions);
      case 'yearBuilt':
        return (
          <NumberField
            label={field.label}
            minValue={value.yearBuiltMin}
            maxValue={value.yearBuiltMax}
            onChangeMin={next => update({ yearBuiltMin: next })}
            onChangeMax={next => update({ yearBuiltMax: next })}
          />
        );
      case 'pickedVolume':
        return (
          <NumberField
            label={field.label}
            minValue={value.pickedVolumeMin}
            maxValue={value.pickedVolumeMax}
            onChangeMin={next => update({ pickedVolumeMin: next })}
            onChangeMax={next => update({ pickedVolumeMax: next })}
          />
        );
      case 'totalSqft':
        return (
          <NumberField
            label={field.label}
            minValue={value.totalSqftMin}
            maxValue={value.totalSqftMax}
            onChangeMin={next => update({ totalSqftMin: next })}
            onChangeMax={next => update({ totalSqftMax: next })}
          />
        );
      case 'operationalSqft':
        return (
          <NumberField
            label={field.label}
            minValue={value.operationalSqftMin}
            maxValue={value.operationalSqftMax}
            onChangeMin={next => update({ operationalSqftMin: next })}
            onChangeMax={next => update({ operationalSqftMax: next })}
          />
        );
      case 'docks':
        return (
          <NumberField
            label={field.label}
            minValue={value.docksMin}
            maxValue={value.docksMax}
            onChangeMin={next => update({ docksMin: next })}
            onChangeMax={next => update({ docksMax: next })}
          />
        );
      case 'docksEng':
        return (
          <NumberField
            label={field.label}
            minValue={value.docksEngMin}
            maxValue={value.docksEngMax}
            onChangeMin={next => update({ docksEngMin: next })}
            onChangeMax={next => update({ docksEngMax: next })}
          />
        );
      case 'docksNoEng':
        return (
          <NumberField
            label={field.label}
            minValue={value.docksNoEngMin}
            maxValue={value.docksNoEngMax}
            onChangeMin={next => update({ docksNoEngMin: next })}
            onChangeMax={next => update({ docksNoEngMax: next })}
          />
        );
      case 'driveIn':
        return (
          <NumberField
            label={field.label}
            minValue={value.driveInMin}
            maxValue={value.driveInMax}
            onChangeMin={next => update({ driveInMin: next })}
            onChangeMax={next => update({ driveInMax: next })}
          />
        );
      case 'bottleLines':
        return (
          <NumberField
            label={field.label}
            minValue={value.bottleLinesMin}
            maxValue={value.bottleLinesMax}
            onChangeMin={next => update({ bottleLinesMin: next })}
            onChangeMax={next => update({ bottleLinesMax: next })}
          />
        );
      case 'canLines':
        return (
          <NumberField
            label={field.label}
            minValue={value.canLinesMin}
            maxValue={value.canLinesMax}
            onChangeMin={next => update({ canLinesMin: next })}
            onChangeMax={next => update({ canLinesMax: next })}
          />
        );
      case 'build1':
        return renderMulti('build1', field.label, buildSite1Options, buildSite1SearchOptions);
      case 'build2':
        return renderMulti('build2', field.label, buildSite2Options, buildSite2SearchOptions);
      case 'bulkSite':
        return renderMulti('bulkSite', field.label, bulkSiteOptions, bulkSiteSearchOptions);
      default:
        return null;
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType={Platform.OS === 'web' ? 'none' : 'slide'}
      onRequestClose={onClose}
    >
      <TouchableOpacity style={styles.backdrop} onPress={onClose} activeOpacity={1} />
      <View style={styles.drawer}>
        <View style={styles.header}>
          <View style={styles.headerText}>
            <Text style={styles.title}>More Filters</Text>
            <Text style={styles.subtitle}>Search and filter across the full location profile.</Text>
          </View>
          <View style={styles.headerActions}>
            {appliedCount > 0 && (
              <View style={styles.countBadge}>
                <Text style={styles.countBadgeText}>{appliedCount}</Text>
              </View>
            )}
            <TouchableOpacity onPress={onClose} style={styles.closeBtn} activeOpacity={0.7}>
              <MaterialIcons name="close" size={20} color={AdminColors.textPrimary} />
            </TouchableOpacity>
          </View>
        </View>

        <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
          <View style={styles.searchWrap}>
            <MaterialIcons name="search" size={16} color={AdminColors.textMuted} />
            <TextInput
              style={styles.searchInput}
              placeholder="Search attributes..."
              placeholderTextColor={AdminColors.textMuted}
              value={search}
              onChangeText={setSearch}
            />
            {search.length > 0 && (
              <TouchableOpacity onPress={() => setSearch('')} style={styles.searchClear} activeOpacity={0.7}>
                <MaterialIcons name="close" size={16} color={AdminColors.textSecondary} />
              </TouchableOpacity>
            )}
          </View>

          {filteredSections.length > 0 ? (
            filteredSections.map(section => (
              <View key={section.title} style={styles.section}>
                <Text style={styles.sectionTitle}>{section.title}</Text>
                <Text style={styles.sectionSubtitle}>{section.description}</Text>
                <View style={styles.sectionGrid}>
                  {section.fields.map(field => (
                    <View key={field.key} style={styles.fieldWrap}>
                      {renderSectionField(field)}
                    </View>
                  ))}
                </View>
              </View>
            ))
          ) : (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateText}>No attributes match your search.</Text>
            </View>
          )}
        </ScrollView>

        <View style={styles.footer}>
          <TouchableOpacity style={[styles.footerBtn, styles.resetBtn]} onPress={handleReset} activeOpacity={0.7}>
            <Text style={styles.resetBtnText}>Reset</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.footerBtn, styles.applyBtn]} onPress={handleApply} activeOpacity={0.7}>
            <Text style={styles.applyBtnText}>Apply</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(17, 37, 63, 0.35)',
  },
  drawer: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: '94%',
    maxWidth: 590,
    backgroundColor: AdminColors.bgPage,
    shadowColor: '#000',
    shadowOffset: { width: -10, height: 0 },
    shadowOpacity: 0.18,
    shadowRadius: 18,
    elevation: 18,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 14,
    paddingHorizontal: 22,
    paddingVertical: 18,
    backgroundColor: AdminColors.bgSurface,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.borderDefault,
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.bold,
  },
  subtitle: {
    marginTop: 4,
    fontSize: 12,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  countBadge: {
    minWidth: 24,
    height: 24,
    borderRadius: 12,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: AdminColors.blue25,
  },
  countBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: AdminColors.blue900,
    fontFamily: FontFamily.bold,
  },
  closeBtn: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: AdminColors.gray25,
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    flex: 1,
  },
  bodyContent: {
    paddingHorizontal: 22,
    paddingVertical: 16,
    gap: 18,
  },
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    height: 40,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    backgroundColor: AdminColors.bgSurface,
    paddingHorizontal: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    padding: 0,
  },
  searchClear: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: AdminColors.gray25,
  },
  section: {
    gap: 10,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: '700',
    color: AdminColors.blue900,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    fontFamily: FontFamily.bold,
    paddingBottom: 7,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.borderDefault,
  },
  sectionSubtitle: {
    marginTop: -2,
    fontSize: 12,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
  },
  sectionGrid: {
    gap: 12,
  },
  fieldWrap: {
    width: '100%',
  },
  fieldGroup: {
    gap: 6,
  },
  rangeGroup: {
    gap: 6,
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.bold,
  },
  rangeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  rangeInput: {
    flex: 1,
    height: 40,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 7,
    backgroundColor: AdminColors.bgSurface,
    paddingHorizontal: 10,
    fontSize: 13,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
  },
  rangeDash: {
    fontSize: 12,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.medium,
  },
  emptyState: {
    paddingVertical: 28,
    alignItems: 'center',
  },
  emptyStateText: {
    fontSize: 13,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
    textAlign: 'center',
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 22,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: AdminColors.borderDefault,
    backgroundColor: AdminColors.bgSurface,
  },
  footerBtn: {
    flex: 1,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  resetBtn: {
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    backgroundColor: AdminColors.bgSurface,
  },
  resetBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.bold,
  },
  applyBtn: {
    backgroundColor: AdminColors.blue800,
  },
  applyBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: AdminColors.textOnDark,
    fontFamily: FontFamily.bold,
  },
});

export default MoreAttributesDrawer;